import type {
  ProposedDataChange,
  ProposeDataChangeRequest,
  LookupValue,
  CreateLookupValueRequest,
  UpdateLookupValueRequest,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

type SuccessEnvelope<T> = { data: T };

export type { ProposedDataChange } from '@logspot-platform/platform-types';
export type { LookupValue } from '@logspot-platform/platform-types';

// A curated company record (attributes) as returned by the super-admin
// attributes search/detail endpoints.
export type CompanyRecord = {
  domain: string;
  name: string;
  attributes: Record<string, unknown>;
  version: number;
};

// Super-admin data management: staged, accept-to-apply edits to the
// platform-maintained data (personas, company attributes). Mirrors the
// backend admin endpoints (api/config/data-changes.ts, attributes.ts).
export const DataManagementService = {
  // Open (unapplied) proposals across every entity type.
  listChanges: async (): Promise<ProposedDataChange[]> => {
    const r = await api.get<SuccessEnvelope<{ changes: ProposedDataChange[] }>>(
      '/config/data-changes',
    );
    return r.data.changes;
  },

  // Stage a proposal — or, with `apply_direct: true`, write it live now.
  propose: async (
    input: ProposeDataChangeRequest,
  ): Promise<ProposedDataChange | { applied: true }> => {
    const r = await api.post<
      SuccessEnvelope<ProposedDataChange | { applied: true }>
    >('/config/data-changes', input);
    return r.data;
  },

  // Apply a staged change (drift-checked; may 409 with a message).
  applyChange: async (id: string): Promise<ProposedDataChange> => {
    const r = await api.post<SuccessEnvelope<{ change: ProposedDataChange }>>(
      `/config/data-changes/${id}/apply`,
      {},
    );
    return r.data.change;
  },

  // Dismiss (discard) a staged change.
  dismissChange: async (id: string): Promise<ProposedDataChange> => {
    const r = await api.post<SuccessEnvelope<{ change: ProposedDataChange }>>(
      `/config/data-changes/${id}/dismiss`,
      {},
    );
    return r.data.change;
  },

  // Server-side attributes search (empty array when the term is empty).
  searchCompanies: async (q: string): Promise<CompanyRecord[]> => {
    const r = await api.get<
      SuccessEnvelope<{ companies: CompanyRecord[] }>
    >(`/config/companies?q=${encodeURIComponent(q)}`);
    return r.data.companies;
  },

  // A single curated company record by domain.
  getCompany: async (domain: string): Promise<CompanyRecord> => {
    const r = await api.get<SuccessEnvelope<{ company: CompanyRecord }>>(
      `/config/companies/${encodeURIComponent(domain)}`,
    );
    return r.data.company;
  },

  // ── Lookups ───────────
  //
  // DB-backed allowed values for GTM categorical attributes (persona
  // department/seniority/buyer-role). Anything in use is delete-protected by
  // the backend (DELETE → 409 with a message when `usage_count > 0`).

  // All values for a category (sorted by the server).
  listLookupValues: async (
    category: string,
  ): Promise<LookupValue[]> => {
    const r = await api.get<SuccessEnvelope<{ values: LookupValue[] }>>(
      `/config/lookups/${encodeURIComponent(category)}`,
    );
    return r.data.values;
  },

  // Add a new value to a category (409 if a duplicate value already exists).
  addLookupValue: async (
    input: CreateLookupValueRequest,
  ): Promise<LookupValue> => {
    const r = await api.post<SuccessEnvelope<{ value: LookupValue }>>(
      '/config/lookups',
      input,
    );
    return r.data.value;
  },

  // Update a value's label/sort_order (the `value` itself is immutable).
  updateLookupValue: async (
    id: string,
    input: UpdateLookupValueRequest,
  ): Promise<LookupValue> => {
    const r = await api.put<SuccessEnvelope<{ value: LookupValue }>>(
      `/config/lookups/${encodeURIComponent(id)}`,
      input,
    );
    return r.data.value;
  },

  // Delete a value (409 with a message when it's still in use).
  deleteLookupValue: async (id: string): Promise<{ ok: true }> => {
    const r = await api.delete<SuccessEnvelope<{ ok: true }>>(
      `/config/lookups/${encodeURIComponent(id)}`,
    );
    return r.data;
  },

  // Delete a persona by key (409 when attached to a customer profile).
  deletePersona: async (
    key: string,
    scope = 'global',
  ): Promise<{ ok: true }> => {
    const r = await api.delete<SuccessEnvelope<{ ok: true }>>(
      `/admin/personas/${encodeURIComponent(key)}?scope=${scope}`,
    );
    return r.data;
  },

  // Delete a curated company record by domain (409 when attached to an account).
  deleteCompany: async (domain: string): Promise<{ ok: true }> => {
    const r = await api.delete<SuccessEnvelope<{ ok: true }>>(
      `/config/companies/${encodeURIComponent(domain)}`,
    );
    return r.data;
  },
};
